import style from "./index.module.scss";
import WrapperContent from "../../components/shared/WrapperContent";
import React, { useEffect, useState } from "react";
import NotificationCard from "../../components/notification/NotificationCard";
import NotificationFilter from "../../components/notification/NotificationFilter";
import DefaultPagination from "../../components/shared/DefaultPagination";
import { requestErrorDisplay } from "../../utils";
import { Notification } from "../../services";
import Loading from "../../components/shared/Loading/Loading";
import { useTranslation } from "react-i18next";
import { useLocalizedDate } from "../../hooks/useLocalizedDate";
import HeaderContentNew from "../../components/shared/HeaderContentNew/HeaderContentNew";
import DefaultPaginationNew from "../../components/shared/DefaultPaginationNew";
import OrdersChatHub from "../../components/shared/chat/OrdersChatHub/OrdersChatHub";

const ITEMS_LIMIT = 10;

const NotificationPage = () => {
  const { t } = useTranslation();
  const formatDate = useLocalizedDate();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLimit, changeLimit] = useState(10);
  const [notificationsTotal, setNotificationsTotal] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedReadStatus, setSelectedReadStatus] = useState("all");
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [chatModalState, setChatModalState] = useState({
    isOpen: false,
    orderId: null,
    serviceType: "order",
  });

  const getNotifications = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: selectedLimit,
      };

      if (selectedType !== "all") {
        params.type = selectedType;
      }

      if (selectedReadStatus === "read") {
        params.isRead = true;
      } else if (selectedReadStatus === "unread") {
        params.isRead = false;
      }

      const trimmed = searchQuery.trim();
      if (trimmed.length > 0) {
        params.search = trimmed;
      }

      const response = await Notification.getNotificationsList(params);
      const responseData = response.data;

      setNotifications(responseData.rows || []);
      setNotificationsTotal(responseData.total);
      setSelectedNotifications([]);
    } catch (error) {
      requestErrorDisplay(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNotifications();
  }, [currentPage, selectedType, selectedReadStatus, selectedLimit]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      getNotifications();
    }, 500); // 500 мс задержка

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedType, selectedReadStatus, searchQuery, selectedLimit]);

  const handleSelectNotification = (id) => {
    setSelectedNotifications((prev) =>
      prev.includes(id) ? prev.filter((nId) => nId !== id) : [...prev, id],
    );
  };

  const markAsRead = async (id) => {
    try {
      await Notification.markUserNotificationAsRead(id);
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.id === id ? { ...notification, isRead: true } : notification,
        ),
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
      requestErrorDisplay(error);
    }
  };

  const markSelectedAsRead = async () => {
    if (selectedNotifications.length === 0) return;

    const unreadSelectedIds = selectedNotifications.filter((id) => {
      const notification = notifications.find((n) => n.id === id);
      return notification && !notification.isRead;
    });

    if (unreadSelectedIds.length === 0) return;

    try {
      await Promise.all(unreadSelectedIds.map((id) => Notification.markUserNotificationAsRead(id)));
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          unreadSelectedIds.includes(notification.id) ? { ...notification, isRead: true } : notification,
        ),
      );
      setSelectedNotifications([]);
    } catch (error) {
      console.error("Error marking selected notifications as read:", error);
      requestErrorDisplay(error);
    }
  };

  const markAllAsRead = async () => {
    const unreadNotificationIds = notifications
      .filter((notification) => !notification.isRead)
      .map((notification) => notification.id);

    if (unreadNotificationIds.length === 0) return;

    try {
      await Promise.all(unreadNotificationIds.map((id) => Notification.markUserNotificationAsRead(id)));
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) => ({ ...notification, isRead: true })),
      );
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      requestErrorDisplay(error);
    }
  };

  const groupedNotifications = Array.isArray(notifications)
    ? notifications.reduce((result, notification) => {
        const date = formatDate(notification.createdAt, { day: "numeric", month: "long", year: "numeric" });
        if (!result[date]) result[date] = [];
        result[date].push(notification);
        return result;
      }, {})
    : {};

  const hasUnreadNotifications = notifications.some((n) => !n.isRead);

  return (
    <>
      <HeaderContentNew
        displayTitle
        title={t("notificationPage.header.title")}
        practicalTitle={
          selectedNotifications.length > 0
            ? t("notificationPage.buttons.markSelectedRead")
            : t("notificationPage.buttons.markAllRead")
        }
        onPracticalBtnClick={() => {
          if (selectedNotifications.length > 0) {
            markSelectedAsRead();
          } else {
            markAllAsRead();
          }
        }}
        isPlusIcon={false}
      />
      <WrapperContent>
        <div style={{ display: "flex", gap: "26px" }}>
          <div style={{ flex: "3" }}>
            {isLoading ? (
              <Loading inBlock />
            ) : Object.keys(groupedNotifications).length === 0 ? (
              <div className={style.emptyState}>
                <p>{t("notificationPage.noNotifications")}</p>
              </div>
            ) : (
              Object.entries(groupedNotifications).map(([date, notificationsForDate]) => (
                <div key={date} className={style.notificationGroup}>
                  <h3 className={style.dateHeader}>{date}</h3>
                  <div className={style.notificationsList}>
                    {notificationsForDate.map((notification) => (
                      <NotificationCard
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={markAsRead}
                        isSelected={selectedNotifications.includes(notification.id)}
                        onSelect={handleSelectNotification}
                        onOpenChat={(orderId, serviceType) => {
                          setChatModalState({
                            isOpen: true,
                            orderId,
                            serviceType,
                          });
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          <NotificationFilter
            selectedType={selectedType}
            selectedReadStatus={selectedReadStatus}
            onTypeChange={setSelectedType}
            onReadStatusChange={setSelectedReadStatus}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>
        <DefaultPaginationNew
          total={notificationsTotal}
          currentPage={currentPage}
          limit={selectedLimit}
          changePage={setCurrentPage}
          changeLimit={changeLimit}
          isLoading={isLoading}
        />
      </WrapperContent>
      <OrdersChatHub
        isOpen={chatModalState.isOpen}
        orderId={chatModalState.orderId}
        onClose={() => setChatModalState({ isOpen: false, orderId: null, serviceType: "order" })}
        serviceType={chatModalState.serviceType}
      />
    </>
  );
};

export default NotificationPage;
